import { convertQty, normalizeComparableUom } from './uomConvert.js';

export type StockProjectorKind = 'stock_in' | 'stock_out' | 'stock_reserved';

export interface StockProjectorEvent {
  id?: string | null;
  /**
   * Local SQLite keeps the client UUID in `id` and the authoritative row UUID
   * here. Remote/server callers normally expose that UUID directly as `id`.
   */
  server_id?: string | null;
  client_uuid?: string | null;
  tenant_id?: string | null;
  business_id?: string | null;
  kind: string;
  server_rev?: number | null;
  rev?: number | null;
  server_ts?: string | null;
  client_ts?: string | null;
  payload?: Readonly<Record<string, unknown>> | null;
}

export interface StockProjectionScope {
  tenantId: string;
  businessId: string | null;
}

export interface StockProjectionOptions {
  scope: StockProjectionScope;
  /**
   * Optional authoritative stock UoM for each food. When absent, the first
   * accepted movement establishes the balance UoM; compatible mass/volume
   * movements are still converted into it.
   */
  canonicalUomByFood?: Readonly<Record<string, string>>;
  /**
   * Most stock workflows consume by food+lot and do not carry a storage unit
   * on every outbound leg. Enable this only in views that have complete,
   * symmetric storage coordinates on both stock_in and stock_out.
   */
  includeStorageUnit?: boolean;
  /**
   * Historical events written before business-scoped warehouses have no
   * business_id. Single-site clients previously treated them as belonging to
   * the active site; keep that compatibility explicit so a projector rollout
   * does not make their stock disappear.
   */
  includeLegacyUnscopedBusiness?: boolean;
  /**
   * Reservations whose lifecycle is owned by a master row rather than an
   * immutable stock_reserved event (currently draft shipments). They are
   * folded into the same availableQty read model so every consumer sees the
   * same committed + pending availability.
   */
  externalReservations?: readonly StockProjectionReservation[];
}

export interface StockProjectionReservation {
  id?: string | null;
  tenantId: string;
  businessId: string | null;
  foodId: string;
  batchLot: string | null;
  storageUnitId?: string | null;
  qty: number;
  uom: string;
}

export interface StockBalance {
  key: string;
  tenantId: string;
  businessId: string | null;
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  storageUnitId: string | null;
  uom: string;
  onHandQty: number;
  /**
   * Carried book value of the physical balance. Null means that at least one
   * non-zero leg could not be valued safely; callers must not substitute the
   * latest purchase price because produced/transferred stock can have a
   * different carried cost.
   */
  onHandValue: number | null;
  averageUnitCost: number | null;
  valueCurrency: string | null;
  unvaluedOnHandQty: number;
  reservedQty: number;
  availableQty: number;
  closed: boolean;
  eventCount: number;
  lastEventId: string | null;
}

export type StockQuarantineCode =
  | 'missing_tenant'
  | 'business_scope_mismatch'
  | 'missing_payload'
  | 'missing_food_id'
  | 'partial_stock_quantity'
  | 'invalid_quantity'
  | 'negative_quantity'
  | 'missing_uom'
  | 'incompatible_uom';

export interface QuarantinedStockEvent {
  code: StockQuarantineCode;
  message: string;
  eventId: string | null;
  event: StockProjectorEvent;
}

export interface StockProjectionWarning {
  code:
    | 'duplicate_event'
    | 'invalid_external_reservation'
    | 'incompatible_external_reservation'
    | 'reservation_underflow'
    | 'negative_on_hand'
    | 'reservation_exceeds_on_hand'
    | 'invalid_valuation'
    | 'incompatible_valuation_uom'
    | 'mixed_valuation_currency'
    | 'incomplete_valuation';
  message: string;
  eventId?: string | null;
  balanceKey?: string;
}

export interface StockProjectionDiagnostics {
  inputEvents: number;
  inScopeEvents: number;
  appliedEvents: number;
  ignoredOutOfScope: number;
  ignoredUnsupportedKinds: number;
  duplicateEvents: number;
  legacyQuantityEvents: number;
  additiveQuantityEvents: number;
  convertedQuantityEvents: number;
  compensationEvents: number;
  /**
   * Server-confirmed document receipts whose exact, explicitly linked
   * cancellation is also present. The immutable events remain in the audit
   * log, but neither leg participates in stock balance or UoM quarantine.
   */
  neutralizedLinkedMovementPairs: number;
  neutralizedLinkedMovementEvents: number;
  ignoredLegacyRepairSourceEvents: number;
  ignoredPreRebaseEvents: number;
  ignoredRebaseSourceEvents: number;
  legacyUnscopedBusinessEvents: number;
  externalReservations: number;
  appliedExternalReservations: number;
  warnings: StockProjectionWarning[];
}

export interface StockProjectionResult {
  balances: StockBalance[];
  diagnostics: StockProjectionDiagnostics;
  quarantine: QuarantinedStockEvent[];
}

interface MutableBalance extends StockBalance {
  onHandQty: number;
  reservedQty: number;
  valuedOnHandQty: number;
  knownValue: number;
  valuationTainted: boolean;
}

interface MovementQuantity {
  qty: number;
  uom: string;
  source: 'additive' | 'legacy';
}

interface MovementValuation {
  unitCost: number | null;
  currency: string | null;
  invalid: boolean;
}

interface AppliedMovementValuation {
  kind: 'stock_in' | 'stock_out';
  uom: string;
  unitCost: number;
  currency: string;
}

interface OrderedEvent {
  event: StockProjectorEvent;
  index: number;
}

const STOCK_KINDS = new Set<string>(['stock_in', 'stock_out', 'stock_reserved']);

function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean === '' ? null : clean;
}

const normalizeUom = normalizeComparableUom;

function eventId(event: StockProjectorEvent): string | null {
  return cleanString(event.id) ?? cleanString(event.client_uuid);
}

function eventIdentity(event: StockProjectorEvent): string | null {
  const clientUuid = cleanString(event.client_uuid);
  if (clientUuid) return `client:${event.tenant_id ?? ''}:${clientUuid}`;
  const id = cleanString(event.id);
  if (id) return `id:${id}`;
  return null;
}

function eventReferenceKeys(event: StockProjectorEvent): string[] {
  return [
    cleanString(event.server_id),
    cleanString(event.id),
    cleanString(event.client_uuid),
  ].filter((value): value is string => value !== null);
}

interface TypedEventReference {
  kind: 'server' | 'client';
  value: string;
}

function authoritativeEventId(event: StockProjectorEvent): string | null {
  return cleanString(event.server_id) ?? cleanString(event.id);
}

function eventClientReference(event: StockProjectorEvent): string | null {
  return cleanString(event.client_uuid)
    ?? (cleanString(event.server_id) ? cleanString(event.id) : null);
}

function typedReversalReferences(
  payload: Readonly<Record<string, unknown>>,
): TypedEventReference[] {
  const fields: ReadonlyArray<{
    kind: TypedEventReference['kind'];
    key: string;
  }> = [
    { kind: 'client', key: 'reverses_event_client_uuid' },
    { kind: 'server', key: 'reverses_event_id' },
    { kind: 'client', key: 'compensates_event_client_uuid' },
    { kind: 'server', key: 'compensates_event_id' },
    { kind: 'client', key: 'cancels_event_client_uuid' },
    { kind: 'server', key: 'cancels_event_id' },
    { kind: 'client', key: 'cancelled_event_client_uuid' },
    { kind: 'server', key: 'cancelled_event_id' },
    { kind: 'client', key: 'corrects_event_client_uuid' },
    { kind: 'server', key: 'corrects_event_id' },
  ];
  return fields.flatMap(({ kind, key }) => {
    const value = cleanString(payload[key]);
    return value ? [{ kind, value }] : [];
  });
}

function reversalReference(
  payload: Readonly<Record<string, unknown>>,
): string | null {
  return cleanString(payload.reverses_event_client_uuid)
    ?? cleanString(payload.reverses_event_id)
    ?? cleanString(payload.compensates_event_client_uuid)
    ?? cleanString(payload.compensates_event_id)
    ?? cleanString(payload.cancels_event_client_uuid)
    ?? cleanString(payload.cancels_event_id)
    ?? cleanString(payload.cancelled_event_client_uuid)
    ?? cleanString(payload.cancelled_event_id);
}

function eventBusinessId(
  event: StockProjectorEvent,
  payload: Readonly<Record<string, unknown>>,
): { businessId: string | null; mismatch: boolean } {
  const topLevel = cleanString(event.business_id);
  const payloadValue = cleanString(payload.business_id);
  return {
    businessId: topLevel ?? payloadValue,
    mismatch: topLevel !== null && payloadValue !== null && topLevel !== payloadValue,
  };
}

function eventOrderValue(event: StockProjectorEvent): number | null {
  const rev = event.server_rev ?? event.rev;
  return typeof rev === 'number' && Number.isFinite(rev) ? rev : null;
}

function confirmedServerRevision(event: StockProjectorEvent): number | null {
  const serverRevision = event.server_rev;
  if (
    typeof serverRevision === 'number'
    && Number.isInteger(serverRevision)
    && serverRevision > 0
  ) return serverRevision;
  const localRevision = event.rev;
  return typeof localRevision === 'number'
    && Number.isInteger(localRevision)
    && localRevision > 0
    && cleanString(event.server_id) !== null
    ? localRevision
    : null;
}

function eventTimestamp(event: StockProjectorEvent): number {
  const raw = event.server_ts ?? event.client_ts;
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function commandPosition(event: StockProjectorEvent): {
  commandId: string;
  sequence: number;
} | null {
  const commandId = cleanString(event.payload?.command_id);
  const rawSequence = event.payload?.command_seq;
  if (
    !commandId
    || typeof rawSequence !== 'number'
    || !Number.isInteger(rawSequence)
    || rawSequence <= 0
  ) return null;
  return { commandId, sequence: rawSequence };
}

function compareEvents(a: OrderedEvent, b: OrderedEvent): number {
  const ar = eventOrderValue(a.event);
  const br = eventOrderValue(b.event);
  if (ar !== null && br !== null && ar !== br) return ar - br;
  if (ar !== null && br === null) return -1;
  if (ar === null && br !== null) return 1;
  const at = eventTimestamp(a.event);
  const bt = eventTimestamp(b.event);
  if (at !== bt) return at - bt;
  const ac = commandPosition(a.event);
  const bc = commandPosition(b.event);
  if (ac && bc && ac.commandId === bc.commandId && ac.sequence !== bc.sequence) {
    return ac.sequence - bc.sequence;
  }
  return a.index - b.index;
}

function readMovementQuantity(
  payload: Readonly<Record<string, unknown>>,
): MovementQuantity | StockQuarantineCode {
  const hasStockQty = Object.prototype.hasOwnProperty.call(payload, 'stock_qty');
  const hasStockUom = Object.prototype.hasOwnProperty.call(payload, 'stock_uom');
  if (hasStockQty !== hasStockUom) return 'partial_stock_quantity';

  const source = hasStockQty ? 'additive' : 'legacy';
  const rawQty = source === 'additive' ? payload.stock_qty : payload.qty;
  const rawUom = source === 'additive' ? payload.stock_uom : payload.uom;
  if (typeof rawQty !== 'number' || !Number.isFinite(rawQty)) return 'invalid_quantity';
  if (rawQty < 0) return 'negative_quantity';
  const uom = cleanString(rawUom);
  if (!uom) return 'missing_uom';
  return { qty: rawQty, uom: normalizeUom(uom), source };
}

function readMovementValuation(
  payload: Readonly<Record<string, unknown>>,
): MovementValuation {
  const hasStockUnitCost = Object.prototype.hasOwnProperty.call(
    payload,
    'stock_unit_cost',
  );
  const rawCost = hasStockUnitCost ? payload.stock_unit_cost : payload.unit_cost;
  if (rawCost === null || rawCost === undefined) {
    return { unitCost: null, currency: null, invalid: false };
  }
  if (
    typeof rawCost !== 'number'
    || !Number.isFinite(rawCost)
    || rawCost < 0
  ) {
    return { unitCost: null, currency: null, invalid: true };
  }

  const rawCurrency = cleanString(payload.source_currency)
    ?? cleanString(payload.currency)
    ?? 'EUR';
  const currency = rawCurrency.toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) {
    return { unitCost: rawCost, currency: null, invalid: true };
  }
  return { unitCost: rawCost, currency, invalid: false };
}

function quarantineMessage(code: StockQuarantineCode): string {
  switch (code) {
    case 'missing_tenant': return 'Evento stock senza tenant_id verificabile.';
    case 'business_scope_mismatch': return 'business_id del record e del payload non coincidono.';
    case 'missing_payload': return 'Evento stock senza payload.';
    case 'missing_food_id': return 'Evento stock senza food_id.';
    case 'partial_stock_quantity': return 'stock_qty e stock_uom devono essere presenti insieme.';
    case 'invalid_quantity': return 'Quantità stock assente, non numerica o non finita.';
    case 'negative_quantity': return 'La quantità deve essere una magnitudine non negativa; il segno deriva dal kind.';
    case 'missing_uom': return 'Unità di misura stock assente.';
    case 'incompatible_uom': return 'Unità di misura incompatibile con la coordinata canonica del saldo.';
  }
}

function roundQty(value: number): number {
  const rounded = Math.round(value * 1_000_000_000_000) / 1_000_000_000_000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function roundValue(value: number): number {
  const rounded = Math.round(value * 1_000_000_000_000) / 1_000_000_000_000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function almostZero(value: number): boolean {
  return Math.abs(value) <= 1e-9;
}

function isCompensation(payload: Readonly<Record<string, unknown>>): boolean {
  return payload.void_compensation === true
    || payload.compensation === true
    || cleanString(payload.reverses_event_client_uuid) !== null
    || cleanString(payload.reverses_event_id) !== null
    || cleanString(payload.compensates_event_id) !== null
    || cleanString(payload.cancels_event_id) !== null
    || cleanString(payload.cancelled_event_id) !== null;
}

const LEGACY_LABEL_VOID_REPAIR_PREFIX = 'legacy-label-void-repair:v1:';
const STOCK_RECONCILIATION_MARKER_PREFIX = 'stock-reconciliation:v1:';
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isConfirmedStockReconciliationMarker(
  event: StockProjectorEvent,
): boolean {
  const payload = event.payload;
  if (
    event.kind !== 'stock_in'
    || confirmedServerRevision(event) === null
    || !payload
    || payload.movement_class !== 'lot_state'
    || payload.lot_state_action !== 'update_metadata'
    || payload.economic_effect !== 'stock_only'
  ) return false;
  const reference = cleanString(payload.source_line_ref);
  if (
    !reference
    || !reference.startsWith(STOCK_RECONCILIATION_MARKER_PREFIX)
    || !UUID_PATTERN.test(
      reference.slice(STOCK_RECONCILIATION_MARKER_PREFIX.length),
    )
  ) return false;
  const movement = readMovementQuantity(payload);
  return typeof movement !== 'string' && movement.qty === 0;
}

function legacyRepairSourceKey(
  tenantId: string,
  sourceEventId: string,
): string {
  return `${tenantId}:${sourceEventId.toLowerCase()}`;
}

function repairedLegacySourceKey(
  event: StockProjectorEvent,
): string | null {
  if (
    event.kind !== 'stock_in'
    // Remote rows expose `server_rev`; confirmed SQLite rows expose `rev`
    // together with `server_id`. A local revision without writeback identity
    // remains optimistic and must never replace historical evidence.
    || confirmedServerRevision(event) === null
    || !event.payload
    || event.payload.movement_class !== 'receive'
    || event.payload.economic_effect !== 'stock_only'
    || event.payload.command_version !== 1
  ) return null;
  const tenantId = cleanString(event.tenant_id);
  const sourceLineRef = cleanString(event.payload.source_line_ref);
  if (!tenantId || !sourceLineRef?.startsWith(LEGACY_LABEL_VOID_REPAIR_PREFIX)) {
    return null;
  }
  const sourceEventId = sourceLineRef.slice(
    LEGACY_LABEL_VOID_REPAIR_PREFIX.length,
  );
  if (!UUID_PATTERN.test(sourceEventId)) return null;
  return legacyRepairSourceKey(tenantId, sourceEventId);
}

interface ConfirmedLinkedMovementPair {
  sourcePosition: number;
  neutralizerPosition: number;
}

function effectiveBusinessIdForScope(
  event: StockProjectorEvent,
  payload: Readonly<Record<string, unknown>>,
  options: StockProjectionOptions,
): string | null | undefined {
  const business = eventBusinessId(event, payload);
  if (business.mismatch) return undefined;
  const effectiveBusinessId = business.businessId === null
    && options.scope.businessId !== null
    && options.includeLegacyUnscopedBusiness === true
    ? options.scope.businessId
    : business.businessId;
  return effectiveBusinessId === options.scope.businessId
    ? effectiveBusinessId
    : undefined;
}

function isTrustedReceiptNeutralizer(
  source: StockProjectorEvent,
  neutralizer: StockProjectorEvent,
): boolean {
  const sourcePayload = source.payload;
  const neutralizerPayload = neutralizer.payload;
  if (!sourcePayload || !neutralizerPayload) return false;

  const sourceDocumentId = cleanString(sourcePayload.document_id);
  if (!sourceDocumentId) return false;

  const legacyCancellation = (
    (
      neutralizerPayload.reason === 'import_cancelled'
      || neutralizerPayload.reason === 'replaced_by_new_document'
    )
    && cleanString(neutralizerPayload.cancelled_document_id)
      === sourceDocumentId
    && cleanString(neutralizerPayload.document_id) === null
    && cleanString(neutralizerPayload.cancels_event_id)
      === authoritativeEventId(source)
    && cleanString(neutralizerPayload.movement_class) === null
    && cleanString(neutralizerPayload.economic_effect) === null
    && neutralizerPayload.void_compensation !== true
  );
  const canonicalReverse = (
    neutralizerPayload.movement_class === 'reverse'
    && neutralizerPayload.economic_effect === 'reversal'
    && neutralizerPayload.void_compensation === true
    && cleanString(neutralizerPayload.document_id) === sourceDocumentId
  );
  const correctionSource = (
    neutralizerPayload.movement_class === 'correction'
    && neutralizerPayload.economic_effect === 'stock_only'
    && neutralizerPayload.correction_leg === 'source'
    && neutralizerPayload.reason === 'lot_correction'
    && cleanString(neutralizerPayload.document_id) === sourceDocumentId
    && cleanString(neutralizerPayload.corrects_event_id)
      === authoritativeEventId(source)
  );
  return legacyCancellation || canonicalReverse || correctionSource;
}

function sameReceiptCoordinates(
  source: StockProjectorEvent,
  neutralizer: StockProjectorEvent,
): boolean {
  const sourcePayload = source.payload;
  const neutralizerPayload = neutralizer.payload;
  if (!sourcePayload || !neutralizerPayload) return false;
  const sourceMovement = readMovementQuantity(sourcePayload);
  const neutralizerMovement = readMovementQuantity(neutralizerPayload);
  if (
    typeof sourceMovement === 'string'
    || typeof neutralizerMovement === 'string'
    || sourceMovement.qty <= 0
    || neutralizerMovement.qty <= 0
  ) return false;

  return (
    cleanString(sourcePayload.food_id) !== null
    && cleanString(sourcePayload.food_id)
      === cleanString(neutralizerPayload.food_id)
    && cleanString(sourcePayload.batch_lot)
      === cleanString(neutralizerPayload.batch_lot)
    && cleanString(sourcePayload.storage_unit_id)
      === cleanString(neutralizerPayload.storage_unit_id)
    && cleanString(sourcePayload.supplier_id)
      === cleanString(neutralizerPayload.supplier_id)
    && normalizeUom(sourceMovement.uom)
      === normalizeUom(neutralizerMovement.uom)
    && sourceMovement.qty === neutralizerMovement.qty
    && sourcePayload.reserve_released !== true
    && neutralizerPayload.reserve_released !== true
    && !Object.prototype.hasOwnProperty.call(sourcePayload, 'lot_closed')
    && !Object.prototype.hasOwnProperty.call(neutralizerPayload, 'lot_closed')
    && cleanString(sourcePayload.lot_state_action) === null
    && cleanString(neutralizerPayload.lot_state_action) === null
    && sourcePayload.movement_class !== 'uom_rebase'
    && neutralizerPayload.movement_class !== 'uom_rebase'
  );
}

function exactValuationOrLegacyNull(
  sourcePayload: Readonly<Record<string, unknown>>,
  neutralizerPayload: Readonly<Record<string, unknown>>,
): boolean {
  const neutralizerHasStockCost = Object.prototype.hasOwnProperty.call(
    neutralizerPayload,
    'stock_unit_cost',
  );
  const neutralizerCost = neutralizerHasStockCost
    ? neutralizerPayload.stock_unit_cost
    : neutralizerPayload.unit_cost;
  // The legacy document cancellation adapter intentionally omitted value. Its
  // exact source link still proves which carried value is being neutralized.
  if (neutralizerCost === null || neutralizerCost === undefined) return true;

  const sourceHasStockCost = Object.prototype.hasOwnProperty.call(
    sourcePayload,
    'stock_unit_cost',
  );
  const sourceCost = sourceHasStockCost
    ? sourcePayload.stock_unit_cost
    : sourcePayload.unit_cost;
  if (
    typeof sourceCost !== 'number'
    || !Number.isFinite(sourceCost)
    || typeof neutralizerCost !== 'number'
    || !Number.isFinite(neutralizerCost)
    || sourceCost !== neutralizerCost
  ) return false;

  const sourceCurrency = (
    cleanString(sourcePayload.source_currency)
    ?? cleanString(sourcePayload.currency)
    ?? 'EUR'
  ).toUpperCase();
  const neutralizerCurrency = (
    cleanString(neutralizerPayload.source_currency)
    ?? cleanString(neutralizerPayload.currency)
    ?? 'EUR'
  ).toUpperCase();
  return sourceCurrency === neutralizerCurrency;
}

/**
 * A document cancellation is a historical fact, not an active movement that
 * the operator must "repair". Recognize only a server-confirmed, exact pair:
 * explicit event reference, same scope/document/stock coordinates, opposite
 * receipt direction and source-before-cancellation order.
 */
function confirmedLinkedMovementPairs(
  ordered: readonly OrderedEvent[],
  options: StockProjectionOptions,
): ConfirmedLinkedMovementPair[] {
  const sourcesByServerReference = new Map<string, number[]>();
  const sourcesByClientReference = new Map<string, number[]>();
  const canonicalPositions = new Set<number>();
  const seenIdentities = new Set<string>();
  for (const [position, { event }] of ordered.entries()) {
    const identity = eventIdentity(event);
    if (identity && seenIdentities.has(identity)) continue;
    if (identity) seenIdentities.add(identity);
    canonicalPositions.add(position);
    const payload = event.payload;
    if (
      event.kind !== 'stock_in'
      || confirmedServerRevision(event) === null
      || cleanString(event.tenant_id) !== options.scope.tenantId
      || !payload
      || effectiveBusinessIdForScope(event, payload, options) === undefined
      || isCompensation(payload)
    ) continue;
    const serverReference = authoritativeEventId(event);
    if (serverReference) {
      const positions = sourcesByServerReference.get(serverReference) ?? [];
      positions.push(position);
      sourcesByServerReference.set(serverReference, positions);
    }
    const clientReference = eventClientReference(event);
    if (clientReference) {
      const positions = sourcesByClientReference.get(clientReference) ?? [];
      positions.push(position);
      sourcesByClientReference.set(clientReference, positions);
    }
  }

  const pairedPositions = new Set<number>();
  const pairs: ConfirmedLinkedMovementPair[] = [];
  for (const [neutralizerPosition, { event: neutralizer }] of ordered.entries()) {
    if (!canonicalPositions.has(neutralizerPosition)) continue;
    const payload = neutralizer.payload;
    if (
      neutralizer.kind !== 'stock_out'
      || confirmedServerRevision(neutralizer) === null
      || cleanString(neutralizer.tenant_id) !== options.scope.tenantId
      || !payload
      || effectiveBusinessIdForScope(neutralizer, payload, options)
        === undefined
    ) continue;

    const references = typedReversalReferences(payload);
    if (references.length === 0) continue;
    const referencedPositions = new Set<number>();
    let referencesAreUnique = true;
    for (const reference of references) {
      const positions = (
        reference.kind === 'server'
          ? sourcesByServerReference
          : sourcesByClientReference
      ).get(reference.value) ?? [];
      if (positions.length !== 1) {
        referencesAreUnique = false;
        break;
      }
      referencedPositions.add(positions[0]);
    }
    if (!referencesAreUnique || referencedPositions.size !== 1) continue;

    const [sourcePosition] = [...referencedPositions];
    const source = ordered[sourcePosition]?.event;
    const sourceRevision = source
      ? confirmedServerRevision(source)
      : null;
    const neutralizerRevision = confirmedServerRevision(neutralizer);
    if (
      !source
      || sourceRevision === null
      || neutralizerRevision === null
      || neutralizerRevision <= sourceRevision
      || sourcePosition >= neutralizerPosition
      || pairedPositions.has(sourcePosition)
      || pairedPositions.has(neutralizerPosition)
      || source.tenant_id !== neutralizer.tenant_id
      || effectiveBusinessIdForScope(source, source.payload ?? {}, options)
        !== effectiveBusinessIdForScope(neutralizer, payload, options)
      || !isTrustedReceiptNeutralizer(source, neutralizer)
      || !sameReceiptCoordinates(source, neutralizer)
      || !exactValuationOrLegacyNull(source.payload ?? {}, payload)
    ) continue;

    pairedPositions.add(sourcePosition);
    pairedPositions.add(neutralizerPosition);
    pairs.push({ sourcePosition, neutralizerPosition });
  }
  return pairs;
}

export function stockBalanceKey(input: {
  businessId: string | null;
  foodId: string;
  batchLot: string | null;
  storageUnitId?: string | null;
}): string {
  return JSON.stringify([
    input.businessId,
    input.foodId,
    input.batchLot,
    input.storageUnitId ?? null,
  ]);
}

/**
 * Pure, uncapped stock materializer.
 *
 * The event kind is the only source of movement direction. Compensation flags
 * are audit metadata and never invert a movement a second time. New additive
 * `stock_qty/stock_uom` coordinates win over legacy `qty/uom`; the two pairs
 * are never summed together.
 */
export function projectStock(
  events: readonly StockProjectorEvent[],
  options: StockProjectionOptions,
): StockProjectionResult {
  const diagnostics: StockProjectionDiagnostics = {
    inputEvents: events.length,
    inScopeEvents: 0,
    appliedEvents: 0,
    ignoredOutOfScope: 0,
    ignoredUnsupportedKinds: 0,
    duplicateEvents: 0,
    legacyQuantityEvents: 0,
    additiveQuantityEvents: 0,
    convertedQuantityEvents: 0,
    compensationEvents: 0,
    neutralizedLinkedMovementPairs: 0,
    neutralizedLinkedMovementEvents: 0,
    ignoredLegacyRepairSourceEvents: 0,
    ignoredPreRebaseEvents: 0,
    ignoredRebaseSourceEvents: 0,
    legacyUnscopedBusinessEvents: 0,
    externalReservations: 0,
    appliedExternalReservations: 0,
    warnings: [],
  };
  const quarantine: QuarantinedStockEvent[] = [];
  const balances = new Map<string, MutableBalance>();
  const valuationByEventReference = new Map<string, AppliedMovementValuation>();
  const seenEvents = new Set<string>();
  const ordered = events.map((event, index) => ({ event, index })).sort(compareEvents);
  const neutralizedLinkedPairs = confirmedLinkedMovementPairs(
    ordered,
    options,
  );
  const neutralizedLinkedPositions = new Set(
    neutralizedLinkedPairs.flatMap((pair) => [
      pair.sourcePosition,
      pair.neutralizerPosition,
    ]),
  );
  diagnostics.neutralizedLinkedMovementPairs =
    neutralizedLinkedPairs.length;
  const repairedLegacySourceEvents = new Set<string>();
  for (const { event } of ordered) {
    const sourceKey = repairedLegacySourceKey(event);
    if (sourceKey) repairedLegacySourceEvents.add(sourceKey);
  }
  const rebaseCommands = new Map<string, {
    commandId: string;
    firstSnapshotPosition: number;
    lastPosition: number;
  }>();
  const reconciliationCutovers = new Map<string, number>();
  ordered.forEach(({ event }, position) => {
    const payload = event.payload;
    if (payload && isConfirmedStockReconciliationMarker(event)) {
      const tenantId = cleanString(event.tenant_id);
      const foodId = cleanString(payload.food_id);
      const business = eventBusinessId(event, payload);
      if (tenantId && foodId && !business.mismatch) {
        const coordinate = JSON.stringify([tenantId, foodId]);
        reconciliationCutovers.set(
          coordinate,
          Math.max(reconciliationCutovers.get(coordinate) ?? -1, position),
        );
      }
    }
    if (
      !payload
      || payload.movement_class !== 'uom_rebase'
      || event.kind !== 'stock_in'
      || payload.uom_rebase_snapshot !== true
    ) return;
    const tenantId = cleanString(event.tenant_id);
    const foodId = cleanString(payload.food_id);
    const commandId = cleanString(payload.command_id);
    if (!tenantId || !foodId || !commandId) return;
    const business = eventBusinessId(event, payload);
    if (business.mismatch) return;
    // foods.default_uom is tenant-wide, therefore its cutover boundary must
    // also be tenant-wide. A marker committed in one site retires the proven
    // zero history of every other site; the DB guard rejects the command when
    // another site still carries stock or reservations.
    const coordinate = JSON.stringify([tenantId, foodId]);
    const previous = rebaseCommands.get(coordinate);
    if (!previous || position > previous.lastPosition) {
      rebaseCommands.set(coordinate, {
        commandId,
        firstSnapshotPosition: previous?.commandId === commandId
          ? Math.min(previous.firstSnapshotPosition, position)
          : position,
        lastPosition: position,
      });
    } else if (previous.commandId === commandId) {
      previous.firstSnapshotPosition = Math.min(
        previous.firstSnapshotPosition,
        position,
      );
      previous.lastPosition = Math.max(previous.lastPosition, position);
    }
  });

  const reject = (event: StockProjectorEvent, code: StockQuarantineCode, message?: string): void => {
    quarantine.push({
      code,
      message: message ?? quarantineMessage(code),
      eventId: eventId(event),
      event,
    });
  };

  for (const [orderedPosition, { event }] of ordered.entries()) {
    if (!STOCK_KINDS.has(event.kind)) {
      diagnostics.ignoredUnsupportedKinds += 1;
      continue;
    }

    const tenantId = cleanString(event.tenant_id);
    if (!tenantId) {
      reject(event, 'missing_tenant');
      continue;
    }
    if (tenantId !== options.scope.tenantId) {
      diagnostics.ignoredOutOfScope += 1;
      continue;
    }

    const payload = event.payload;
    if (!payload) {
      reject(event, 'missing_payload');
      continue;
    }
    const business = eventBusinessId(event, payload);
    if (business.mismatch) {
      reject(event, 'business_scope_mismatch');
      continue;
    }
    const effectiveBusinessId = business.businessId === null
      && options.scope.businessId !== null
      && options.includeLegacyUnscopedBusiness === true
      ? options.scope.businessId
      : business.businessId;
    if (effectiveBusinessId !== options.scope.businessId) {
      diagnostics.ignoredOutOfScope += 1;
      continue;
    }
    if (business.businessId === null && effectiveBusinessId !== null) {
      diagnostics.legacyUnscopedBusinessEvents += 1;
    }
    diagnostics.inScopeEvents += 1;

    const identity = eventIdentity(event);
    if (identity && seenEvents.has(identity)) {
      diagnostics.duplicateEvents += 1;
      diagnostics.warnings.push({
        code: 'duplicate_event',
        message: 'Evento duplicato ignorato dal projector.',
        eventId: eventId(event),
      });
      continue;
    }
    if (identity) seenEvents.add(identity);

    if (neutralizedLinkedPositions.has(orderedPosition)) {
      diagnostics.neutralizedLinkedMovementEvents += 1;
      if (isCompensation(payload)) diagnostics.compensationEvents += 1;
      continue;
    }

    const isRepairedLegacySource = eventReferenceKeys(event).some(
      (reference) => repairedLegacySourceEvents.has(
        legacyRepairSourceKey(tenantId, reference),
      ),
    );
    if (isRepairedLegacySource) {
      diagnostics.ignoredLegacyRepairSourceEvents += 1;
      continue;
    }

    const foodId = cleanString(payload.food_id);
    if (!foodId) {
      reject(event, 'missing_food_id');
      continue;
    }
    const reconciliationCutover = reconciliationCutovers.get(
      JSON.stringify([tenantId, foodId]),
    );
    if (reconciliationCutover !== undefined) {
      if (orderedPosition < reconciliationCutover) {
        diagnostics.ignoredPreRebaseEvents += 1;
        continue;
      }
      if (
        orderedPosition === reconciliationCutover
        && isConfirmedStockReconciliationMarker(event)
      ) {
        diagnostics.appliedEvents += 1;
        continue;
      }
    }
    const rebase = rebaseCommands.get(JSON.stringify([tenantId, foodId]));
    if (rebase && orderedPosition < rebase.firstSnapshotPosition) {
      const preRebaseMovement = readMovementQuantity(payload);
      const configuredUom = cleanString(
        options.canonicalUomByFood?.[foodId],
      );
      const alreadyInTargetUom = (
        typeof preRebaseMovement !== 'string'
        && configuredUom !== null
        && normalizeUom(preRebaseMovement.uom)
          === normalizeUom(configuredUom)
        && payload.movement_class !== 'uom_rebase'
      );
      // A rebase collapses the old coordinate into a new snapshot, but stock
      // that was already recorded in the destination UoM must survive the
      // cutover. Those movements were quarantined only because the food master
      // still advertised the old UoM; dropping them here would silently erase
      // valid loads (for example the later 72 pz Coca Cola Zero receipt).
      if (!alreadyInTargetUom) {
        diagnostics.ignoredPreRebaseEvents += 1;
        continue;
      }
    }
    if (payload.movement_class === 'uom_rebase') {
      if (
        !rebase
        || cleanString(payload.command_id) !== rebase.commandId
        || event.kind !== 'stock_in'
        || payload.uom_rebase_snapshot !== true
      ) {
        diagnostics.ignoredRebaseSourceEvents += 1;
        continue;
      }
    }
    const movement = readMovementQuantity(payload);
    if (typeof movement === 'string') {
      reject(event, movement);
      continue;
    }
    if (
      payload.movement_class === 'uom_rebase'
      && payload.uom_rebase_snapshot === true
      && payload.uom_rebase_zero === true
      && movement.qty === 0
    ) {
      diagnostics.appliedEvents += 1;
      continue;
    }

    const batchLot = cleanString(payload.batch_lot);
    const storageUnitId = options.includeStorageUnit
      ? cleanString(payload.storage_unit_id)
      : null;
    const key = stockBalanceKey({
      businessId: effectiveBusinessId,
      foodId,
      batchLot,
      storageUnitId,
    });
    const configuredUom = cleanString(options.canonicalUomByFood?.[foodId]);
    const current = balances.get(key);
    const targetUom = normalizeUom(configuredUom ?? current?.uom ?? movement.uom);
    const converted = convertQty(movement.qty, movement.uom, targetUom);
    if (converted === null) {
      reject(
        event,
        'incompatible_uom',
        `${movement.uom} non è convertibile in ${targetUom} per ${foodId}.`,
      );
      continue;
    }
    if (movement.uom !== targetUom) diagnostics.convertedQuantityEvents += 1;
    if (movement.source === 'additive') diagnostics.additiveQuantityEvents += 1;
    else diagnostics.legacyQuantityEvents += 1;
    if (isCompensation(payload)) diagnostics.compensationEvents += 1;

    const balance: MutableBalance = current ?? {
      key,
      tenantId,
      businessId: effectiveBusinessId,
      foodId,
      batchLot,
      expirationDate: null,
      storageUnitId,
      uom: targetUom,
      onHandQty: 0,
      onHandValue: 0,
      averageUnitCost: null,
      valueCurrency: null,
      unvaluedOnHandQty: 0,
      reservedQty: 0,
      availableQty: 0,
      closed: false,
      eventCount: 0,
      lastEventId: null,
      valuedOnHandQty: 0,
      knownValue: 0,
      valuationTainted: false,
    };
    const qty = converted;
    const kind = event.kind as StockProjectorKind;
    const valuation = readMovementValuation(payload);
    let unitCostInTarget: number | null = null;
    let valuationCurrency = valuation.currency;
    let valuationUomIncompatible = false;

    if (valuation.unitCost !== null && valuation.currency !== null) {
      const targetUnitCostFactor = convertQty(1, targetUom, movement.uom);
      if (targetUnitCostFactor === null) {
        valuationUomIncompatible = true;
      } else {
        unitCostInTarget = valuation.unitCost * targetUnitCostFactor;
      }
    } else if (!valuation.invalid && isCompensation(payload)) {
      const reference = reversalReference(payload);
      const original = reference
        ? valuationByEventReference.get(reference)
        : null;
      const isOppositeMovement = original
        && (
          (kind === 'stock_in' && original.kind === 'stock_out')
          || (kind === 'stock_out' && original.kind === 'stock_in')
        );
      if (original && isOppositeMovement) {
        const targetUnitCostFactor = convertQty(1, targetUom, original.uom);
        if (targetUnitCostFactor === null) {
          valuationUomIncompatible = true;
        } else {
          unitCostInTarget = original.unitCost * targetUnitCostFactor;
          valuationCurrency = original.currency;
        }
      }
    }

    if (valuation.invalid) {
      balance.valuationTainted = true;
      diagnostics.warnings.push({
        code: 'invalid_valuation',
        message: 'Costo o valuta del movimento non validi; valorizzazione del lotto sospesa.',
        eventId: eventId(event),
        balanceKey: key,
      });
    }
    if (valuationUomIncompatible) {
      balance.valuationTainted = true;
      diagnostics.warnings.push({
        code: 'incompatible_valuation_uom',
        message: `Il costo del movimento non è convertibile nel saldo in ${targetUom}.`,
        eventId: eventId(event),
        balanceKey: key,
      });
    }

    if (kind === 'stock_in') {
      balance.onHandQty = roundQty(balance.onHandQty + qty);
      if (
        unitCostInTarget === null
        || valuationCurrency === null
        || valuation.invalid
        || valuationUomIncompatible
      ) {
        balance.unvaluedOnHandQty = roundQty(
          balance.unvaluedOnHandQty + qty,
        );
      } else {
        if (
          balance.valueCurrency !== null
          && balance.valueCurrency !== valuationCurrency
        ) {
          balance.valuationTainted = true;
          diagnostics.warnings.push({
            code: 'mixed_valuation_currency',
            message: 'Il lotto contiene movimenti in valute diverse; valorizzazione sospesa.',
            eventId: eventId(event),
            balanceKey: key,
          });
        } else {
          balance.valueCurrency = valuationCurrency;
        }
        balance.valuedOnHandQty = roundQty(
          balance.valuedOnHandQty + qty,
        );
        balance.knownValue = roundValue(
          balance.knownValue + (qty * unitCostInTarget),
        );
      }
      const expirationDate = cleanString(payload.expiration_date);
      if (expirationDate) balance.expirationDate = expirationDate;
    } else if (kind === 'stock_out') {
      const onHandBefore = balance.onHandQty;
      balance.onHandQty = roundQty(balance.onHandQty - qty);
      if (
        unitCostInTarget !== null
        && valuationCurrency !== null
        && !valuation.invalid
        && !valuationUomIncompatible
      ) {
        if (
          balance.valueCurrency !== null
          && balance.valueCurrency !== valuationCurrency
        ) {
          balance.valuationTainted = true;
          diagnostics.warnings.push({
            code: 'mixed_valuation_currency',
            message: 'Il movimento in uscita usa una valuta diversa dal lotto.',
            eventId: eventId(event),
            balanceKey: key,
          });
        }
        if (qty > balance.valuedOnHandQty + 1e-9) {
          balance.valuationTainted = true;
        }
        balance.valuedOnHandQty = roundQty(
          Math.max(0, balance.valuedOnHandQty - qty),
        );
        balance.knownValue = roundValue(
          balance.knownValue - (qty * unitCostInTarget),
        );
      } else if (qty > 0) {
        if (balance.unvaluedOnHandQty + 1e-9 >= qty) {
          balance.unvaluedOnHandQty = roundQty(
            Math.max(0, balance.unvaluedOnHandQty - qty),
          );
        } else if (almostZero(balance.unvaluedOnHandQty)) {
          const averageUnitCost = balance.valuedOnHandQty > 0
            ? balance.knownValue / balance.valuedOnHandQty
            : null;
          if (averageUnitCost === null || qty > balance.valuedOnHandQty + 1e-9) {
            balance.valuationTainted = true;
          } else {
            balance.valuedOnHandQty = roundQty(
              balance.valuedOnHandQty - qty,
            );
            balance.knownValue = roundValue(
              balance.knownValue - (qty * averageUnitCost),
            );
            unitCostInTarget = averageUnitCost;
            valuationCurrency = balance.valueCurrency;
          }
        } else {
          // With mixed valued/unvalued stock and no carried cost, there is no
          // defensible allocation rule. Preserve quantity, fail closed on value.
          balance.valuationTainted = true;
          const unvaluedConsumed = Math.min(
            qty,
            balance.unvaluedOnHandQty,
          );
          balance.unvaluedOnHandQty = roundQty(
            balance.unvaluedOnHandQty - unvaluedConsumed,
          );
          const valuedConsumed = qty - unvaluedConsumed;
          if (valuedConsumed > 0 && balance.valuedOnHandQty > 0) {
            const averageUnitCost = balance.knownValue
              / balance.valuedOnHandQty;
            balance.valuedOnHandQty = roundQty(
              Math.max(0, balance.valuedOnHandQty - valuedConsumed),
            );
            balance.knownValue = roundValue(
              balance.knownValue - (valuedConsumed * averageUnitCost),
            );
          }
        }
      }
      if (qty > onHandBefore + 1e-9) balance.valuationTainted = true;
    }

    if (
      (kind === 'stock_in' || kind === 'stock_out')
      && unitCostInTarget !== null
      && valuationCurrency !== null
    ) {
      const applied: AppliedMovementValuation = {
        kind,
        uom: targetUom,
        unitCost: unitCostInTarget,
        currency: valuationCurrency,
      };
      for (const reference of eventReferenceKeys(event)) {
        valuationByEventReference.set(reference, applied);
      }
    }

    const releasesReservation = payload.reserve_released === true;
    if (kind === 'stock_reserved' || (kind === 'stock_out' && releasesReservation)) {
      const reserveDelta = kind === 'stock_reserved' && !releasesReservation ? qty : -qty;
      const nextReserved = roundQty(balance.reservedQty + reserveDelta);
      if (nextReserved < 0) {
        diagnostics.warnings.push({
          code: 'reservation_underflow',
          message: 'Rilascio riserva superiore alla quantità riservata; riserva limitata a zero.',
          eventId: eventId(event),
          balanceKey: key,
        });
        balance.reservedQty = 0;
      } else {
        balance.reservedQty = nextReserved;
      }
    }

    if (payload.lot_closed === true) balance.closed = true;
    else if (payload.lot_closed === false) balance.closed = false;
    balance.eventCount += 1;
    balance.lastEventId = eventId(event);
    balances.set(key, balance);
    diagnostics.appliedEvents += 1;
  }

  for (const reservation of options.externalReservations ?? []) {
    diagnostics.externalReservations += 1;
    if (
      reservation.tenantId !== options.scope.tenantId
      || reservation.businessId !== options.scope.businessId
    ) {
      continue;
    }

    const foodId = cleanString(reservation.foodId);
    const reservationUom = cleanString(reservation.uom);
    if (
      !foodId
      || !reservationUom
      || !Number.isFinite(reservation.qty)
      || reservation.qty < 0
    ) {
      diagnostics.warnings.push({
        code: 'invalid_external_reservation',
        message: 'Riserva esterna non valida; il lotto resta indisponibile per sicurezza.',
        eventId: reservation.id ?? null,
      });
      const invalidKey = foodId
        ? stockBalanceKey({
            businessId: reservation.businessId,
            foodId,
            batchLot: cleanString(reservation.batchLot),
            storageUnitId: options.includeStorageUnit
              ? cleanString(reservation.storageUnitId)
              : null,
          })
        : null;
      const invalidBalance = invalidKey ? balances.get(invalidKey) : null;
      if (invalidBalance) {
        invalidBalance.reservedQty = Math.max(
          invalidBalance.reservedQty,
          invalidBalance.onHandQty,
        );
      }
      continue;
    }
    if (reservation.qty === 0) continue;

    const batchLot = cleanString(reservation.batchLot);
    const storageUnitId = options.includeStorageUnit
      ? cleanString(reservation.storageUnitId)
      : null;
    const key = stockBalanceKey({
      businessId: reservation.businessId,
      foodId,
      batchLot,
      storageUnitId,
    });
    const current = balances.get(key);
    const configuredUom = cleanString(options.canonicalUomByFood?.[foodId]);
    const targetUom = normalizeUom(
      configuredUom ?? current?.uom ?? reservationUom,
    );
    const converted = convertQty(
      reservation.qty,
      normalizeUom(reservationUom),
      targetUom,
    );
    const balance: MutableBalance = current ?? {
      key,
      tenantId: reservation.tenantId,
      businessId: reservation.businessId,
      foodId,
      batchLot,
      expirationDate: null,
      storageUnitId,
      uom: targetUom,
      onHandQty: 0,
      onHandValue: 0,
      averageUnitCost: null,
      valueCurrency: null,
      unvaluedOnHandQty: 0,
      reservedQty: 0,
      availableQty: 0,
      closed: false,
      eventCount: 0,
      lastEventId: null,
      valuedOnHandQty: 0,
      knownValue: 0,
      valuationTainted: false,
    };
    if (converted === null) {
      diagnostics.warnings.push({
        code: 'incompatible_external_reservation',
        message: `${reservationUom} non è convertibile in ${targetUom}; il lotto resta indisponibile per sicurezza.`,
        eventId: reservation.id ?? null,
        balanceKey: key,
      });
      balance.reservedQty = Math.max(balance.reservedQty, balance.onHandQty);
      balances.set(key, balance);
      continue;
    }

    balance.reservedQty = roundQty(balance.reservedQty + converted);
    balances.set(key, balance);
    diagnostics.appliedExternalReservations += 1;
  }

  const projected = [...balances.values()]
    .map((balance): StockBalance => {
      if (balance.onHandQty < 0) {
        diagnostics.warnings.push({
          code: 'negative_on_hand',
          message: 'Il saldo fisico è negativo.',
          balanceKey: balance.key,
        });
      }
      if (balance.reservedQty > balance.onHandQty) {
        diagnostics.warnings.push({
          code: 'reservation_exceeds_on_hand',
          message: 'La quantità riservata supera il saldo fisico.',
          balanceKey: balance.key,
        });
      }
      const remainingUnvalued = Math.max(
        0,
        Math.min(balance.onHandQty, balance.unvaluedOnHandQty),
      );
      const valuationComplete = (
        balance.onHandQty >= -1e-9
        && balance.knownValue >= -1e-9
        && !balance.valuationTainted
        && almostZero(remainingUnvalued)
        && !(
          balance.onHandQty > 1e-9
          && balance.valueCurrency === null
        )
      );
      const onHandValue = almostZero(balance.onHandQty)
        ? 0
        : valuationComplete
          ? roundValue(Math.max(0, balance.knownValue))
          : null;
      const averageUnitCost = (
        onHandValue !== null
        && balance.onHandQty > 1e-9
      )
        ? roundValue(onHandValue / balance.onHandQty)
        : null;
      if (balance.onHandQty > 1e-9 && onHandValue === null) {
        diagnostics.warnings.push({
          code: 'incomplete_valuation',
          message: 'Il saldo fisico include quantità senza un costo trasportato verificabile.',
          balanceKey: balance.key,
        });
      }
      return {
        key: balance.key,
        tenantId: balance.tenantId,
        businessId: balance.businessId,
        foodId: balance.foodId,
        batchLot: balance.batchLot,
        expirationDate: balance.expirationDate,
        storageUnitId: balance.storageUnitId,
        uom: balance.uom,
        onHandQty: balance.onHandQty,
        onHandValue,
        averageUnitCost,
        valueCurrency: onHandValue === null && balance.onHandQty > 1e-9
          ? null
          : balance.valueCurrency,
        unvaluedOnHandQty: roundQty(remainingUnvalued),
        reservedQty: balance.reservedQty,
        availableQty: balance.closed
          ? 0
          : roundQty(balance.onHandQty - balance.reservedQty),
        closed: balance.closed,
        eventCount: balance.eventCount,
        lastEventId: balance.lastEventId,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));

  return { balances: projected, diagnostics, quarantine };
}
